import React, { useEffect, useState } from "react";
import { Popover, PopoverSurface, PopoverTrigger, Tooltip } from "@fluentui/react-components";
import { ListFilter, X } from "lucide-react";
import IconInput from "../controls/input/iconInput/IconInput";
import { SearchIcon } from "../../modules/default-svg";
import { dateOnlyFormatted } from "../../utils/systemSettings";
import DateField from "../controls/dateField/DateField";
import {
  ACTIVE_STATUSES,
  ALL_STATUSES,
  COMPLETED_STATUS,
  FLAG_OPTIONS,
  FLAG_TONES,
  PRIORITY_OPTIONS,
  PRIORITY_TONES,
  STATUS_OPTIONS,
  STATUS_TONES,
  appliedFilters,
  countAppliedFilters,
  hasCustomFilters,
} from "./filterModel";
import styles from "./resourceInsights.module.scss";

const SEARCH_DEBOUNCE_MS = 300;

const readSettings = () => {
  try {
    return JSON.parse(localStorage.getItem("settings"));
  } catch {
    return null;
  }
};

const toggleValue = (values, value) => (values.includes(value) ? values.filter((item) => item !== value) : [...values, value]);

/** The colours of a pill as style variables the stylesheet reads. */
const toneStyle = (tone) => (tone ? { "--tone": tone.solid, "--tone-soft": tone.soft, "--tone-line": tone.line, "--tone-text": tone.text } : undefined);

/**
 * A choice that can be switched on and off, or the one picked among several. One that stands for something with a
 * colour (a status, a priority, a flag) is a tint of that colour until picked and the colour itself once it is.
 */
function Option({ selected, tone, onClick, children }) {
  return (
    <button type="button" className={`${styles.option} ${tone ? styles.optionTone : ""} ${selected ? styles.optionSelected : ""}`} style={toneStyle(tone)} aria-pressed={selected} onClick={onClick}>
      {children}
    </button>
  );
}

function PanelSection({ title, children }) {
  return (
    <section className={styles.panelSection}>
      <h5>{title}</h5>
      {children}
    </section>
  );
}

/**
 * The panel the filter button opens. What is chosen in it is kept aside and only becomes the filters of the list
 * when Apply is pressed; Reset puts back what Resource Insights starts from at once.
 */
function FiltersPanel({ filters, defaults, projects, onApply, onClose }) {
  const [draft, setDraft] = useState(filters);
  const set = (changes) => setDraft((current) => ({ ...current, ...changes }));

  // The clients of the projects, each once.
  const clients = [...new Map(projects.filter((project) => project.client?.id).map((project) => [project.client.id, project.client])).values()];

  const apply = () => {
    onApply(draft);
    onClose();
  };

  const reset = () => {
    onApply({ ...defaults, search: filters.search });
    onClose();
  };

  return (
    <div className={styles.filterPanel}>
      {clients.length > 1 && (
        <PanelSection title="Client">
          <div className={`${styles.options} ${styles.optionsScroll}`}>
            <Option selected={!draft.clientId} onClick={() => set({ clientId: null })}>
              All clients
            </Option>
            {clients.map((client) => (
              <Option key={client.id} selected={draft.clientId === client.id} onClick={() => set({ clientId: client.id })}>
                {client.name}
              </Option>
            ))}
          </div>
        </PanelSection>
      )}

      {projects.length > 1 && (
        <PanelSection title="Project">
          <div className={`${styles.options} ${styles.optionsScroll}`}>
            <Option selected={!draft.projectId} onClick={() => set({ projectId: null })}>
              All projects
            </Option>
            {projects.map((project) => (
              <Option key={project.id} selected={draft.projectId === project.id} onClick={() => set({ projectId: project.id })}>
                {project.name}
              </Option>
            ))}
          </div>
        </PanelSection>
      )}

      <PanelSection title="Status">
        <div className={styles.options}>
          {STATUS_OPTIONS.map((option) => (
            <Option key={option.value} tone={STATUS_TONES[option.value]} selected={draft.statuses.includes(option.value)} onClick={() => set({ statuses: toggleValue(draft.statuses, option.value) })}>
              {option.name}
            </Option>
          ))}
        </div>
        <div className={styles.presets}>
          <button type="button" onClick={() => set({ statuses: ACTIVE_STATUSES })}>
            Active
          </button>
          <button type="button" onClick={() => set({ statuses: [COMPLETED_STATUS] })}>
            Completed
          </button>
          <button type="button" onClick={() => set({ statuses: ALL_STATUSES })}>
            All
          </button>
        </div>
      </PanelSection>

      <PanelSection title="Priority">
        <div className={styles.options}>
          {PRIORITY_OPTIONS.map((option) => (
            <Option key={option.value} tone={PRIORITY_TONES[option.value]} selected={draft.priorities.includes(option.value)} onClick={() => set({ priorities: toggleValue(draft.priorities, option.value) })}>
              {option.name}
            </Option>
          ))}
        </div>
      </PanelSection>

      <PanelSection title="Show only">
        <div className={styles.options}>
          {FLAG_OPTIONS.map((option) => (
            <Option key={option.key} tone={FLAG_TONES[option.key]} selected={draft[option.key]} onClick={() => set({ [option.key]: !draft[option.key] })}>
              {option.label}
            </Option>
          ))}
        </div>
      </PanelSection>

      <PanelSection title="Period">
        <div className={styles.panelDates}>
          <DateField tooltip="Tasks from" value={draft.startDate} maxDate={draft.endDate} onChange={(startDate) => set({ startDate })} />
          <DateField tooltip="Tasks until" value={draft.endDate} minDate={draft.startDate} onChange={(endDate) => set({ endDate })} />
        </div>
      </PanelSection>

      <div className={styles.panelFooter}>
        <button type="button" className={styles.resetButton} onClick={reset}>
          Reset
        </button>
        <button type="button" className={styles.applyButton} onClick={apply}>
          Apply
        </button>
      </div>
    </div>
  );
}

/**
 * What the list of assignments is filtered by. The name is typed in the field; everything else is chosen in the panel
 * the filter button opens, and what is applied is shown as pills that can be taken off one by one.
 *
 * @param defaults  what Resource Insights starts from: active tasks in its period
 * @param projects  the projects the filter can pick from; with fewer than two there is nothing to choose
 */
export default function AssignmentFilters({ filters, defaults, projects, onChange }) {
  const settings = readSettings();
  const [searchInput, setSearchInput] = useState(filters.search);
  const [open, setOpen] = useState(false);

  // The list follows the name a moment after the typing stops; a name set from outside (a count was pressed) shows.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) onChange({ ...filters, search: searchInput });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);
  useEffect(() => setSearchInput(filters.search), [filters.search]);

  const pills = appliedFilters(filters, projects, (iso) => dateOnlyFormatted(settings, iso));
  const count = countAppliedFilters(filters);

  return (
    <div className={styles.filters}>
      <div className={styles.filterBar}>
        <IconInput icon={<SearchIcon />} placeholder="Search tasks" value={searchInput} handleChange={setSearchInput} />

        <Popover open={open} onOpenChange={(_, data) => setOpen(data.open)} positioning={{ position: "below", align: "end", autoSize: "height" }} trapFocus={false}>
          {/* The tooltip sits inside the element the popover opens from, so the two do not compete for the button. */}
          <PopoverTrigger disableButtonEnhancement>
            <span className={styles.filterButtonWrap}>
              <Tooltip content="Filters" relationship="label" positioning="above">
                <button type="button" className={`${styles.filterButton} ${count > 0 ? styles.filterButtonActive : ""}`}>
                  <ListFilter size={16} />
                  {count > 0 && <span className={styles.filterBadge}>{count}</span>}
                </button>
              </Tooltip>
            </span>
          </PopoverTrigger>

          <PopoverSurface className={styles.filterSurface}>
            {/* Mounted only while open, so it starts from what is applied every time. */}
            <FiltersPanel filters={filters} defaults={defaults} projects={projects} onApply={onChange} onClose={() => setOpen(false)} />
          </PopoverSurface>
        </Popover>
      </div>

      {pills.length > 0 && (
        <div className={styles.appliedPills}>
          {pills.map((pill) => (
            <span key={pill.key} className={`${styles.appliedPill} ${pill.tone ? styles.appliedPillTone : ""}`} style={toneStyle(pill.tone)}>
              {pill.label}
              <button type="button" aria-label={`Remove the filter ${pill.label}`} onClick={() => onChange(pill.remove(filters))}>
                <X size={11} />
              </button>
            </span>
          ))}
          {hasCustomFilters(filters, defaults) && (
            <button type="button" className={styles.clearAll} onClick={() => onChange({ ...defaults })}>
              Reset
            </button>
          )}
        </div>
      )}
    </div>
  );
}
